import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PlantWithId, WaterStatus } from '../../types/plant';
import { getWaterStatus, getWaterStatusText } from '../../utils/formatters';
import { useEffectiveInterval } from '../../hooks/useWateringSchedule';

interface PlantListCardProps {
  plant: PlantWithId;
  reordering?: boolean;
}

/**
 * Editorial horizontal plant card — square photo + text right.
 */
export function PlantListCard({ plant, reordering }: PlantListCardProps) {
  const [hasError, setHasError] = useState(false);
  const effectiveInterval = useEffectiveInterval(plant);

  const displayName = plant.nickname || plant.commonName;
  const status: WaterStatus = getWaterStatus(plant.lastWatered, effectiveInterval);
  const statusText = getWaterStatusText(plant.lastWatered, effectiveInterval);
  const needsWater = status === 'overdue' || status === 'due-soon';

  const content = (
    <>
      {reordering && (
        <span
          className="material-symbols-outlined text-gray-400 text-xl flex-shrink-0 cursor-grab active:cursor-grabbing"
          aria-hidden="true"
        >
          drag_indicator
        </span>
      )}

      {/* Square photo */}
      <div className="w-20 h-20 flex-shrink-0 bg-gray-200 overflow-hidden">
        {hasError ? (
          <div className="w-full h-full flex items-center justify-center">
            <span className="material-symbols-outlined text-gray-400" style={{ fontSize: 24 }} aria-hidden="true">
              eco
            </span>
          </div>
        ) : (
          <img
            src={plant.photoURL}
            alt={displayName}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={() => setHasError(true)}
          />
        )}
      </div>

      {/* Text content */}
      <div className="flex-1 min-w-0">
        <h3 className="text-xl font-display text-editorial-black truncate">
          {displayName}
        </h3>
        {plant.scientificName && (
          <p className="text-sm font-sans italic text-gray-500 mb-2 truncate">
            {plant.scientificName}
          </p>
        )}
        <div className="flex items-center gap-2" role="status" aria-label={statusText}>
          <div
            className={`w-2 h-2 rounded-full ${
              needsWater ? 'bg-status-green animate-pulse' : 'bg-gray-300'
            }`}
            aria-hidden="true"
          />
          {needsWater ? (
            <span className="text-xs font-sans font-bold tracking-wide uppercase text-editorial-black">
              Water today
            </span>
          ) : (
            <span className="text-xs font-sans text-gray-500">
              {statusText}
            </span>
          )}
        </div>
      </div>
    </>
  );

  if (reordering) {
    return (
      <div className="flex items-center gap-4">
        {content}
      </div>
    );
  }

  return (
    <Link
      to={`/plant/${plant.id}`}
      className="group flex items-center gap-4 cursor-pointer"
    >
      {content}
    </Link>
  );
}
